import React, { useMemo } from "react";
import { useMutation } from "react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useDoctorSlots } from "../../hooks/queries/useDoctorAvailability";
import { getDoctorSlots } from "../../services/availability";
import { SLOTS_MAX_RANGE_DAYS, Slot } from "../../services/availability/types";
import { SLOT_PERIODS, addDays, firstSlot, formatDateKey, groupSlotsByPeriod } from "../../utils/availability";

type AppointmentSlotPickerProps = {
  /** Médecin du RDV (celui qu'enregistre la consultation), pas forcément l'utilisateur connecté. */
  doctorId: number | null | undefined;
  date: string;
  selectedTime: string | null | undefined;
  /** Choix d'un créneau ; `date` change quand on saute au prochain créneau libre. */
  onSelect: (date: string, time: string) => void;
};

/**
 * Créneaux libres du médecin pour la date choisie, groupés matin / après-midi
 * (`GET /availability/doctors/{id}/slots`). Jour sans créneau : message et
 * recherche du prochain créneau libre sur 31 jours.
 */
const AppointmentSlotPicker: React.FC<AppointmentSlotPickerProps> = ({ doctorId, date, selectedTime, onSelect }) => {
  const { data: slots = [], isLoading } = useDoctorSlots(doctorId, date, date);
  const periods = useMemo(() => groupSlotsByPeriod(slots), [slots]);

  const nextSlotSearch = useMutation(
    () => {
      const from = addDays(date, 1);
      return getDoctorSlots(doctorId as number, from, addDays(from, SLOTS_MAX_RANGE_DAYS - 1));
    },
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        const next = firstSlot(Array.isArray(result.data) ? result.data : []);
        if (next) {
          onSelect(next.date, next.startTime.slice(0, 5));
        } else {
          toast.info(`Aucun créneau libre dans les ${SLOTS_MAX_RANGE_DAYS} jours suivants`);
        }
      },
    }
  );

  if (doctorId == null) {
    return (
      <p className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500">
        Aucun médecin assigné au patient : créneaux indisponibles.
      </p>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-slate-600">Créneaux libres</span>
        <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
          {formatDateKey(date)}
        </span>
      </div>

      {isLoading ? (
        <p className="py-3 text-center text-xs text-slate-500">Chargement des créneaux…</p>
      ) : slots.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-3 text-center">
          <i className="bx bx-calendar-x text-2xl text-slate-300" aria-hidden />
          <p className="text-sm text-slate-600">Médecin absent ou aucun créneau libre ce jour</p>
          <button
            type="button"
            onClick={() => nextSlotSearch.mutate()}
            disabled={nextSlotSearch.isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-60"
          >
            <i className={cn("bx text-base", nextSlotSearch.isLoading ? "bx-loader-alt animate-spin" : "bx-search")} />
            Prochain créneau libre
          </button>
        </div>
      ) : (
        SLOT_PERIODS.filter((period) => periods[period.value].length > 0).map((period) => (
          <div key={period.value} className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{period.label}</p>
            <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-1.5">
              {periods[period.value].map((slot: Slot) => {
                const time = slot.startTime.slice(0, 5);
                const isSelected = time === selectedTime;
                return (
                  <button
                    key={time}
                    type="button"
                    onClick={() => onSelect(date, time)}
                    aria-pressed={isSelected}
                    className={cn(
                      "rounded-lg border px-1.5 py-1.5 text-[11px] font-medium tabular-nums transition-all",
                      isSelected
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    )}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AppointmentSlotPicker;
